import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Panel, LiveTag, StatBox, Avatar, TokenLogo, VerifiedBadge } from "../components/common/Panel";
import TraceView from "../components/common/TraceView";
import { getChainInfo, getStats, getBlocks, getTxs, getNodes, getTokens, getValidators, getTrace } from "../lib/api";
import { useLiveFeed } from "../hooks/useLiveFeed";
import { shorten, timeAgo, fmtNum, fmtEth, methodOf, methodClass, CHAIN } from "../lib/format";
import { AreaChart, Area, ResponsiveContainer, Tooltip, LineChart, Line } from "recharts";

function MiniTip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "var(--panel-2)", border: "1px solid var(--border-2)", borderRadius: 8, padding: "5px 9px" }}>
      <div className="text-[11px] font-mono" style={{ color: "var(--amber)" }}>{payload[0].value} {unit}</div>
    </div>
  );
}

export default function Dashboard() {
  const [info, setInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [validators, setValidators] = useState([]);
  const [trace, setTrace] = useState(null);
  const [traceTx, setTraceTx] = useState(null);
  const seeded = useRef(false);
  const lastTraced = useRef(null);

  const { blocks, txs, connected, setBlocks, setTxs } = useLiveFeed({ maxBlocks: 10, maxTxs: 12 });

  // seed initial data + poll aggregate stats
  useEffect(() => {
    let alive = true;
    const safe = (fn) => fn().catch(() => null);
    const load = async () => {
      const [i, s, n, tk, v] = await Promise.all([
        safe(getChainInfo), safe(getStats), safe(getNodes), safe(getTokens), safe(getValidators),
      ]);
      if (!alive) return;
      if (i) setInfo(i);
      if (s) setStats(s);
      if (n) setNodes(n.nodes);
      if (tk) setTokens(tk.tokens);
      if (v) setValidators(v.validators);
    };
    const seedFeed = async () => {
      const [b, t] = await Promise.all([safe(() => getBlocks(10)), safe(() => getTxs(12))]);
      if (!alive) return;
      if (b && !seeded.current) setBlocks(b.blocks.map((x) => ({ ...x })));
      if (t && !seeded.current) setTxs(t.transactions.map((x) => ({ ...x })));
      seeded.current = true;
    };
    load(); seedFeed();
    const id = setInterval(load, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [setBlocks, setTxs]);

  // auto-select latest contract-call tx for the trace visualizer
  useEffect(() => {
    const contractTx = txs.find((t) => t.input && t.input !== "0x");
    const target = contractTx || txs[0];
    if (target && target.hash !== lastTraced.current) {
      lastTraced.current = target.hash;
      setTraceTx(target);
      getTrace(target.hash).then((d) => setTrace(d.trace)).catch(() => setTrace(null));
    }
  }, [txs]);

  const areaData = stats?.points?.map((p) => ({ n: p.number, tx: p.txCount })) || [];
  // real-time chart driven by the WebSocket block feed (falls back to polled stats)
  const liveChart = blocks.length
    ? [...blocks].reverse().map((b) => ({ n: b.number, tx: b.txCount }))
    : areaData;
  const liveTxTotal = liveChart.reduce((s, p) => s + (p.tx || 0), 0);
  const onlineNodes = nodes.filter((n) => n.online).length;
  const snapshotToken = tokens[0];
  const maxVBlocks = Math.max(1, ...validators.map((v) => v.recentBlocks || 0));

  return (
    <div className="fade-in" data-testid="dashboard-page">
      {/* Stat row */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-5">
        <StatBox testId="stat-latest-block" label="Latest Block" value={info ? `#${fmtNum(info.latestBlock)}` : "—"} sub="QBFT finalized" icon="fa-solid fa-cube" />
        <StatBox testId="stat-block-time" label="Avg Block Time" value={info ? `${info.blockTime}s` : "—"} sub="3s target period" icon="fa-solid fa-clock" accent="var(--blue)" />
        <StatBox testId="stat-validators" label="Validators" value={info ? info.validatorCount : "—"} sub={`${onlineNodes}/${nodes.length || 4} nodes online`} icon="fa-solid fa-shield-halved" accent="var(--green)" />
        <StatBox testId="stat-tps" label="Throughput" value={stats ? `${stats.tps}` : "—"} sub="tx / sec (20 blk)" icon="fa-solid fa-gauge-high" />
        <StatBox testId="stat-gas" label="Gas Price" value={info ? `${info.gasPriceGwei}` : "—"} sub="Gwei · network fee" icon="fa-solid fa-fire" accent="var(--amber)" />
        <StatBox testId="stat-supply" label="Total Supply" value={info ? fmtNum(info.totalSupply) : "22,500,000"} sub={`${CHAIN.symbol} max supply`} icon="fa-solid fa-coins" accent="var(--green)" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        {/* LEFT: LATEST ACTIVITY STREAM */}
        <div className="xl:col-span-4">
          <Panel title="Latest Activity Stream" action={<LiveTag label={connected ? "Live" : "Polling"} />} testId="panel-activity-stream">
            <div className="section-head"><span className="section-label">Latest Blocks</span><span className="text-[10px] font-mono" style={{ color: "var(--muted-2)" }}>live scrolling</span></div>
            <div className="max-h-[240px] overflow-auto">
              <table className="dt">
                <tbody>
                  {blocks.map((b, idx) => (
                    <tr key={b.number} className={`${idx === 0 ? "row-active" : ""} ${b._new ? "row-new" : ""}`} onClick={() => (window.location.href = `/block/${b.number}`)} data-testid={`block-row-${b.number}`}>
                      <td style={{ width: 90 }}>
                        <Link to={`/block/${b.number}`} className="link-amber mono font-semibold" onClick={(e) => e.stopPropagation()}>#{b.number}</Link>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Avatar address={b.miner} size={16} />
                          <span className="mono text-[11.5px]" style={{ color: "var(--muted)" }}>{shorten(b.miner, 5, 3)}</span>
                        </div>
                      </td>
                      <td style={{ width: 60 }}><span className="pill pill-muted">{b.txCount}</span></td>
                      <td className="mono text-[11.5px] text-right" style={{ color: "var(--muted)", width: 66 }}>{timeAgo(b.timestamp)}</td>
                    </tr>
                  ))}
                  {blocks.length === 0 && <tr><td className="text-center py-6" style={{ color: "var(--muted)" }}>Loading…</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="section-head" style={{ borderTop: "1px solid var(--border)" }}><span className="section-label">Latest Transactions</span><span className="text-[10px] font-mono" style={{ color: "var(--muted-2)" }}>live scrolling</span></div>
            <div className="max-h-[300px] overflow-auto">
              <table className="dt">
                <tbody>
                  {txs.map((t, idx) => {
                    const m = methodOf(t.input);
                    return (
                      <tr key={t.hash} className={`${idx === 0 ? "row-active" : ""} ${t._new ? "row-new" : ""}`} onClick={() => (window.location.href = `/tx/${t.hash}`)} data-testid={`tx-row-${t.hash.slice(0, 10)}`}>
                        <td><Link to={`/tx/${t.hash}`} className="link-amber mono text-[12px]" onClick={(e) => e.stopPropagation()}>{shorten(t.hash, 6, 4)}</Link></td>
                        <td style={{ width: 84 }}><span className={`pill ${methodClass(m)}`}>{m}</span></td>
                        <td className="mono text-[11px]" style={{ color: "var(--muted)" }}>{shorten(t.from, 4, 3)}<i className="fa-solid fa-arrow-right-long mx-1" style={{ color: "var(--muted-2)" }} />{shorten(t.to, 4, 3)}</td>
                        <td className="mono text-[11.5px] text-right" style={{ color: "var(--text)", width: 90 }}>{fmtEth(t.valueEth, 2)} {CHAIN.symbol}</td>
                      </tr>
                    );
                  })}
                  {txs.length === 0 && <tr><td className="text-center py-6" style={{ color: "var(--muted)" }}>Waiting…</td></tr>}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>

        {/* CENTER: TRACE VISUALIZER + SNAPSHOT + ANALYTICS */}
        <div className="xl:col-span-5 flex flex-col gap-5">
          <Panel title="BCC VM Execution Trace Visualizer" subtitle={traceTx ? shorten(traceTx.hash, 8, 6) : ""} action={<span className="pill pill-amber"><i className="fa-solid fa-code-branch" /> callTracer</span>} testId="panel-trace">
            <div className="max-h-[300px] overflow-auto">
              <TraceView trace={trace} />
            </div>
            {traceTx && (
              <div className="px-4 py-3 flex items-center justify-between text-[12px]" style={{ borderTop: "1px solid var(--border)", color: "var(--muted)" }}>
                <span>Tracing: <Link to={`/tx/${traceTx.hash}`} className="link-amber mono">{shorten(traceTx.hash, 8, 6)}</Link></span>
                <span className="mono">total gas {fmtNum(trace?.gasUsed)}</span>
              </div>
            )}
          </Panel>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Panel title="Contract Snapshot" testId="panel-contract-snapshot">
              {snapshotToken ? (
                <div className="p-5 space-y-3">
                  <div className="flex items-center gap-2">
                    <TokenLogo logo={snapshotToken.logo} symbol={snapshotToken.symbol} size={22} />
                    <span className="pill pill-amber">{snapshotToken.symbol}</span>
                    {snapshotToken.verified && <VerifiedBadge small />}
                  </div>
                  <div className="font-display font-bold text-[15px]">{snapshotToken.name}</div>
                  <div className="flex justify-between text-[12.5px]"><span className="eyebrow">Standard</span><span className="mono">BCC-20</span></div>
                  <div className="flex justify-between text-[12.5px]"><span className="eyebrow">Holders</span><span className="mono">{fmtNum(snapshotToken.holders)}</span></div>
                  <div className="flex justify-between text-[12.5px]"><span className="eyebrow">Supply</span><span className="mono">{fmtNum(snapshotToken.totalSupply)}</span></div>
                  <Link to={`/token/${snapshotToken.address}`} className="link-amber text-[12px] block pt-1" data-testid="snapshot-view-token">View token contract →</Link>
                </div>
              ) : <div className="p-8 text-center" style={{ color: "var(--muted)" }}>No contracts yet</div>}
            </Panel>

            <Panel title="Network Analytics" action={<LiveTag label="20 blk" />} testId="panel-analytics">
              <div className="p-4">
                <div className="eyebrow mb-1">Transactions / Block</div>
                <div className="stat-value text-[22px] mb-2" style={{ color: "var(--amber)" }}>{liveTxTotal}</div>
                <div style={{ height: 120 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={liveChart}>
                      <defs>
                        <linearGradient id="txG" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="var(--amber)" stopOpacity={0.55} />
                          <stop offset="100%" stopColor="var(--amber)" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Tooltip content={<MiniTip unit="txs" />} />
                      <Area type="monotone" dataKey="tx" stroke="var(--amber)" strokeWidth={1.8} fill="url(#txG)" isAnimationActive={true} animationDuration={500} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </Panel>
          </div>
        </div>

        {/* RIGHT: NETWORK INSIGHTS */}
        <div className="xl:col-span-3 flex flex-col gap-5">
          <Panel title="Network Insights" action={<LiveTag />} testId="panel-network-insights">
            <div className="section-head"><span className="section-label">Validator Activity</span><span className="text-[10px] font-mono" style={{ color: "var(--muted-2)" }}>QBFT rotation</span></div>
            <div className="px-4 pb-3 space-y-2.5">
              {validators.map((v, i) => (
                <div key={v.address} data-testid={`insight-validator-${i}`}>
                  <div className="flex items-center justify-between text-[11.5px] mb-1">
                    <span className="mono" style={{ color: "var(--muted)" }}>V{i + 1} · {shorten(v.address, 4, 3)}</span>
                    <span className="mono" style={{ color: "var(--text)" }}>{v.recentBlocks} blk</span>
                  </div>
                  <div className="gasbar"><span style={{ width: `${(v.recentBlocks / maxVBlocks) * 100}%` }} /></div>
                </div>
              ))}
              {validators.length === 0 && <div className="text-center py-4" style={{ color: "var(--muted)" }}>Loading…</div>}
            </div>

            <div className="section-head" style={{ borderTop: "1px solid var(--border)" }}><span className="section-label">Throughput</span><span className="text-[10px] font-mono" style={{ color: "var(--amber)" }}>{stats?.tps ?? 0} tps</span></div>
            <div className="px-3 pb-3" style={{ height: 90 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={liveChart}>
                  <Tooltip content={<MiniTip unit="txs" />} />
                  <Line type="monotone" dataKey="tx" stroke="var(--blue)" strokeWidth={1.8} dot={false} isAnimationActive={true} animationDuration={500} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel title="Node Health" action={<span className="pill pill-green"><span className="dot dot-green" />{onlineNodes}/{nodes.length || 4}</span>} testId="panel-node-health">
            <div className="p-2">
              {nodes.map((n) => (
                <div key={n.id} className="flex items-center justify-between px-3 py-2.5" style={{ borderBottom: "1px solid rgba(30,40,49,0.5)" }} data-testid={`node-health-${n.id}`}>
                  <div className="flex items-center gap-2.5">
                    <span className={`dot ${n.online ? "dot-green" : "dot-red"}`} />
                    <span className="font-display text-[12.5px] font-semibold">{n.name}</span>
                  </div>
                  <div className="flex items-center gap-4 mono text-[11.5px]" style={{ color: "var(--muted)" }}>
                    <span>#{fmtNum(n.blockNumber)}</span>
                    <span><i className="fa-solid fa-network-wired mr-1" style={{ color: "var(--muted-2)" }} />{n.peers ?? 0}</span>
                  </div>
                </div>
              ))}
              {nodes.length === 0 && <div className="text-center py-6" style={{ color: "var(--muted)" }}>Loading…</div>}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
